import React, { useState } from 'react';
import { LinkIcon, ShieldIcon } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Field, Select } from '../ui/Field';
import { useNotes } from '../../contexts/NotesContext';
import { shareCandidates } from '../../data/notes';
import type { Note, ShareRole } from '../../types/notes';
interface ShareDialogProps {
  note: Note;
  open: boolean;
  onClose: () => void;
}
export function ShareDialog({
  note,
  open,
  onClose
}: ShareDialogProps) {
  const {
    share,
    setShareRole,
    revokeShare,
    setShareLink
  } = useNotes();
  const [handle, setHandle] = useState('');
  const [role, setRole] = useState<Exclude<ShareRole, 'owner'>>('viewer');
  const available = shareCandidates.filter(person => !note.shares.some(entry => entry.handle === person.handle));
  return <Modal open={open} onClose={onClose} title="Share this note" description={note.isPrivate ? 'This note is private. Nobody else can open it until you deliberately grant access.' : `${note.shares.length} ${note.shares.length === 1 ? 'person has' : 'people have'} access.`} width="lg" footer={<Button variant="secondary" onClick={onClose} data-id="element-3987" data-mp="3n9y7rkl2plfd">
          Done
        </Button>} data-id="element-3986" data-mp="e0ap627w8b39">
      <div className="space-y-5" data-id="element-3988" data-mp="3ibwph1vh1q4y">
        <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_140px_auto] sm:items-end" data-id="element-3989" data-mp="3ddbsrcffhol9">
          <Field label="Campuzon user" htmlFor="share-handle" data-id="element-3990" data-mp="3daz1u9rp03w2">
            <Select id="share-handle" value={handle} onChange={event => setHandle(event.target.value)} data-id="element-3991" data-mp="38cp3xrel2p6r">
              <option value="" data-id="element-3992" data-mp="2ze3nc1so9k8m">Select a person</option>
              {available.map(person => <option key={person.handle} value={person.handle} data-id="element-3993" data-mp="12ltq74y2jwo8">
                  {person.displayName} · @{person.handle}
                </option>)}
            </Select>
          </Field>
          <Field label="Role" htmlFor="share-role" data-id="element-3994" data-mp="1gqanlrty253b">
            <Select id="share-role" value={role} onChange={event => setRole(event.target.value as Exclude<ShareRole, 'owner'>)} data-id="element-3995" data-mp="1goporq0ljzzn">
              <option value="viewer" data-id="element-3996" data-mp="17rywlop615ac">View only</option>
              <option value="editor" data-id="element-3997" data-mp="1copcaax25yv8">Can edit</option>
            </Select>
          </Field>
          <Button variant="primary" disabled={!handle} onClick={() => {
          const person = shareCandidates.find(item => item.handle === handle);
          if (person) share(note.id, person, role);
          setHandle('');
        }} data-id="element-3998" data-mp="20f2wxgdbcvfs">
            Share
          </Button>
        </div>

        <div className="rounded-card border border-line" data-id="element-3999" data-mp="1vdvn7brm1m6h">
          <p className="border-b border-line px-4 py-2.5 text-[12px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-4000" data-mp="f173j03ecwr8">
            Who has access
          </p>
          <ul className="divide-y divide-line" data-id="element-4001" data-mp="ch0p1yh5pnwf">
            <li className="flex flex-wrap items-center gap-3 px-4 py-3" data-id="element-4002" data-mp="cfplnzrmii5u">
              <div className="min-w-0 flex-1" data-id="element-4003" data-mp="7ih3hz6h6e4l">
                <p className="text-[13px] font-medium text-ink" data-id="element-4004" data-mp="7ho4xlho6uxj">{note.ownerName} (you)</p>
                <p className="cz-num text-[12px] text-ink-muted" data-id="element-4005" data-mp="lh6ztclh09rw">Created {note.createdAt}</p>
              </div>
              <Badge tone="brand" data-id="element-4006" data-mp="gjfb0rg4h3rx">Owner</Badge>
            </li>
            {note.shares.map(entry => <li key={entry.id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-id="element-4007" data-mp="3owcstfqs7mjh">
                <div className="min-w-0 flex-1" data-id="element-4008" data-mp="3kyopbob0wlfz">
                  <p className="text-[13px] font-medium text-ink" data-id="element-4009" data-mp="2je3wu99l040">{entry.displayName}</p>
                  <p className="cz-num text-[12px] text-ink-muted" data-id="element-4010" data-mp="3txs8ste3r2v0">
                    @{entry.handle} · granted {entry.grantedOn}
                  </p>
                </div>
                <Select value={entry.role} onChange={event => setShareRole(note.id, entry.id, event.target.value as Exclude<ShareRole, 'owner'>)} aria-label={`Role for ${entry.displayName}`} className="h-8 w-auto py-0 text-[12px]" data-id="element-4011" data-mp="365lavpsqanth">
                  <option value="viewer" data-id="element-4012" data-mp="3oszonskuwc88">View only</option>
                  <option value="editor" data-id="element-4013" data-mp="3tk7z8rxxa3mz">Can edit</option>
                </Select>
                <Button size="sm" variant="danger" onClick={() => revokeShare(note.id, entry.id)} data-id="element-4014" data-mp="1gp7phjvj6je">
                  Remove
                </Button>
              </li>)}
            {note.shares.length === 0 && <li className="px-4 py-3 text-[13px] text-ink-muted" data-id="element-4015" data-mp="beblu1tcmu2b">
                Nobody else. This note is private.
              </li>}
          </ul>
        </div>

        <div className="rounded-card border border-line px-4 py-3.5" data-id="element-4016" data-mp="2d6821oqgos3f">
          <div className="flex flex-wrap items-center gap-3" data-id="element-4017" data-mp="2d8az2sq17lhn">
            <LinkIcon className="h-4 w-4 shrink-0 text-ink-subtle" data-id="element-4018" data-mp="2i6cf14s5v4ow" />
            <div className="min-w-0 flex-1" data-id="element-4019" data-mp="z9yaxizwtn67">
              <p className="text-[13px] font-medium text-ink" data-id="element-4020" data-mp="zbrid0ickbf9">Shareable link</p>
              <p className="cz-num text-[12px] text-ink-muted" data-id="element-4021" data-mp="uif5toqxzv2v">
                {note.shareLink?.active ? `Active since ${note.shareLink.createdOn} · ${note.shareLink.role === 'editor' ? 'can edit' : 'view only'}` : 'No active link'}
              </p>
            </div>
            {note.shareLink?.active ? <Button size="sm" variant="danger" onClick={() => setShareLink(note.id, note.shareLink!.role, false)} data-id="element-4022" data-mp="1e77hhjxsaxii">
                Disable link
              </Button> : <Button size="sm" variant="secondary" onClick={() => setShareLink(note.id, 'viewer', true)} data-id="element-4023" data-mp="149szhfw7q0o3">
                Create view-only link
              </Button>}
          </div>
        </div>

        <p className="flex items-start gap-2 rounded-card border border-line bg-canvas px-4 py-3 text-[12px] leading-5 text-ink-muted" data-id="element-4024" data-mp="3oz8wwtpxsnmp">
          <ShieldIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" data-id="element-4025" data-mp="3qhh0wb3gecin" />
          Access is enforced by the Campuzon Notes service on every request, not by what this
          interface renders. Revoking a role removes access immediately, and private notes are never
          exposed through platform search.
        </p>
      </div>
    </Modal>;
}